// Shared canvas helpers: plot margins, axes, time readout, and axis labels.

export const MARGIN = { left: 56, right: 20, top: 32, bottom: 44 };

export function getPlotArea(w, h) {
    return {
        left: MARGIN.left,
        top: MARGIN.top,
        right: w - MARGIN.right,
        bottom: h - MARGIN.bottom,
        width: w - MARGIN.left - MARGIN.right,
        height: h - MARGIN.top - MARGIN.bottom,
    };
}

export function clearCanvas(ctx, w, h) {
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, w, h);
}

export function drawFrame(ctx, w, h, opts = {}) {
    const {
        xLabel = 'x',
        yLabel = 'y',
        title = '',
        t = null,
        tUnit = '',
        showGrid = true,
    } = opts;

    clearCanvas(ctx, w, h);
    const area = getPlotArea(w, h);

    if (showGrid) {
        ctx.strokeStyle = '#1a1a1a';
        ctx.lineWidth = 1;
        const gridStep = 40;
        for (let x = area.left; x <= area.right; x += gridStep) {
            ctx.beginPath();
            ctx.moveTo(x, area.top);
            ctx.lineTo(x, area.bottom);
            ctx.stroke();
        }
        for (let y = area.top; y <= area.bottom; y += gridStep) {
            ctx.beginPath();
            ctx.moveTo(area.left, y);
            ctx.lineTo(area.right, y);
            ctx.stroke();
        }
    }

    ctx.strokeStyle = '#666';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(area.left, area.top);
    ctx.lineTo(area.left, area.bottom);
    ctx.lineTo(area.right, area.bottom);
    ctx.stroke();

    ctx.fillStyle = '#a1a1aa';
    ctx.font = '13px monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText(xLabel, (area.left + area.right) / 2, h - 28);

    ctx.save();
    ctx.translate(16, (area.top + area.bottom) / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(yLabel, 0, 0);
    ctx.restore();

    if (title) {
        ctx.textAlign = 'left';
        ctx.textBaseline = 'top';
        ctx.fillStyle = '#ffffff';
        ctx.font = '12px monospace';
        ctx.fillText(title, area.left, 8);
    }

    if (t !== null && t !== undefined) {
        ctx.textAlign = 'right';
        ctx.textBaseline = 'top';
        ctx.fillStyle = '#2cbb5d';
        ctx.font = 'bold 13px monospace';
        const tStr = Number.isInteger(t) ? String(t) : t.toFixed(2);
        ctx.fillText(`t = ${tStr}${tUnit ? ` ${tUnit}` : ''}`, area.right, 8);
    }

    return area;
}

export function mapToPlot(area, px, py, xMin, xMax, yMin, yMax) {
    const x = area.left + ((px - xMin) / (xMax - xMin)) * area.width;
    const y = area.bottom - ((py - yMin) / (yMax - yMin)) * area.height;
    return { x, y };
}

export function drawSpatialField(ctx, w, h, n, field, opts = {}) {
    const { xLabel = 'x', yLabel = 'y', t = null, tUnit = '', title = '' } = opts;
    const area = drawFrame(ctx, w, h, { xLabel, yLabel, t, tUnit, title, showGrid: false });
    const cellW = area.width / n;
    const cellH = area.height / n;

    for (let y = 0; y < n; y++) {
        for (let x = 0; x < n; x++) {
            const val = field[y * n + x];
            const t01 = Math.max(0, Math.min(1, val));
            const g = Math.floor(t01 * 200);
            const r = Math.floor((1 - t01) * 180 + t01 * 44);
            const b = Math.floor(t01 * 90 + 30);
            ctx.fillStyle = `rgb(${r},${g + 30},${b})`;
            ctx.fillRect(
                area.left + x * cellW,
                area.top + y * cellH,
                cellW + 0.5,
                cellH + 0.5
            );
        }
    }

    ctx.strokeStyle = '#666';
    ctx.lineWidth = 1.5;
    ctx.strokeRect(area.left, area.top, area.width, area.height);
    return area;
}

export function drawSpatialFieldRgb(ctx, w, h, n, colorFn, opts = {}) {
    const { xLabel = 'x', yLabel = 'y', t = null, tUnit = '', title = '' } = opts;
    const area = drawFrame(ctx, w, h, { xLabel, yLabel, t, tUnit, title, showGrid: false });
    const cellW = area.width / n;
    const cellH = area.height / n;

    for (let y = 0; y < n; y++) {
        for (let x = 0; x < n; x++) {
            const idx = y * n + x;
            ctx.fillStyle = colorFn(idx, x, y);
            ctx.fillRect(
                area.left + x * cellW,
                area.top + y * cellH,
                cellW + 0.5,
                cellH + 0.5
            );
        }
    }

    ctx.strokeStyle = '#666';
    ctx.lineWidth = 1.5;
    ctx.strokeRect(area.left, area.top, area.width, area.height);
    return area;
}
